import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Box, Card, CardActionArea, CircularProgress, Stack, Typography } from "@mui/material";
import Page from "../../../components/Page";
import { Routes } from "../../../routes/RouteConstants";
import { useTenant } from "../../../contexts/TenantContext";
import { storage } from "../../../services/storageService";
import { StorageKeys } from "../../../services/constants/StorageKeys";
import useErrorHandler from "../../../hooks/useErrorHandler";
import { gradientTextSx, tint } from "../../../utils/textStyles";
import { cardSurfaceSx } from "../../../utils/cardSurfaces";
import { fetchSupplies } from "../../supply/useGetSupplies";
import { readProviderChoice, saveProviderChoice, shortNameOf } from "../providerChoice";
import ProviderAvatar from "../components/ProviderAvatar";

// The cards keep one width and the row is centred, so a short last row stands in the middle too.
const CARD_WIDTH = 300;
const CARD_MIN_HEIGHT = 280;

/**
 * One choice: the provider's avatar, their short name large and the full name under it; „Bárki”
 * when there is no `provider`. The current choice keeps a solid edge in the tenant's colour.
 */
const ProviderCard = ({ provider, isCurrent, isPending, disabled, onClick }) => {
  const { displayConfig } = useTenant();
  const surfaceSx = cardSurfaceSx(displayConfig);
  const accent = displayConfig?.primaryColor;
  const title = provider ? shortNameOf(provider) : "Bárki";
  const subtitle = provider
    ? (provider.nickname ? provider.name : null)
    : "akinél van szabad időpont";

  return (
    <Card
      data-testid="provider-card"
      data-provider-id={provider?.id ?? "any"}
      sx={{
        width: { xs: "100%", sm: CARD_WIDTH },
        minHeight: CARD_MIN_HEIGHT,
        display: "flex",
        flexDirection: "column",
        backgroundColor: displayConfig?.sectionBackground,
        color: displayConfig?.color,
        borderRadius: 2,
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        ...surfaceSx,
        ...(isCurrent && { border: `2px solid ${accent}` }),
        "&:hover": { ...surfaceSx["&:hover"], transform: "translateY(-2px)" },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      }}
    >
      {/* No ripple and no tint left behind on the card after a click; the keyboard still gets an
          outline. */}
      <CardActionArea
        disableRipple
        onClick={onClick}
        disabled={disabled}
        aria-current={isCurrent || undefined}
        sx={{
          flex: 1,
          p: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          "& .MuiCardActionArea-focusHighlight": { display: "none" },
          "&.Mui-focusVisible": { outline: `2px solid ${accent}`, outlineOffset: -2 },
        }}
      >
        {/* While loading, the content keeps its place but turns invisible, so the card keeps its
            size, and the spinner takes the card's middle. */}
        <Stack alignItems="center" spacing={1.5} textAlign="center" visibility={isPending ? "hidden" : "visible"}>
          <ProviderAvatar provider={provider} size={96} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={gradientTextSx(displayConfig, displayConfig?.color)}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: tint(displayConfig?.color, 0.7) }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {isPending && (
          <CircularProgress
            size={36}
            aria-label="Betöltés"
            sx={{ color: accent, position: "absolute", top: "calc(50% - 18px)", left: "calc(50% - 18px)" }}
          />
        )}
      </CardActionArea>
    </Card>
  );
};

/**
 * „Kihez szeretnél foglalni?”: the first step of booking on a tenant with several providers, before
 * the services, whose list follows the choice (docs/MULTI_PROVIDER.md). A tenant with one provider
 * has nothing to choose and goes on to its services.
 *
 * Reached from the booking page's „Módosítás” (`state.returnTo`), a new choice goes back there with
 * the chosen service, if the new provider offers it; otherwise to the services.
 */
const ProviderChoicePage = () => {
  const { tenantId, providers, hasSeveralProviders, displayConfig } = useTenant();
  const navigate = useNavigate();
  const returnTo = useLocation().state?.returnTo;
  const { handleError } = useErrorHandler();
  // The provider whose services are being checked; `null` while nothing is.
  const [pendingProviderId, setPendingProviderId] = useState(null);

  if (!hasSeveralProviders) return <Navigate to={Routes.SUPPLY} replace />;

  const currentChoice = readProviderChoice(providers);

  const backToBookingIfOffered = async (providerId) => {
    const selectedSupply = storage.get(StorageKeys.SELECTED_SUPPLY);
    if (returnTo !== Routes.BOOKING || !selectedSupply) {
      navigate(Routes.SUPPLY);
      return;
    }
    // „Bárki” offers every supply.
    if (providerId === null) {
      navigate(Routes.BOOKING);
      return;
    }

    setPendingProviderId(providerId);
    try {
      const offered = await fetchSupplies(tenantId, providerId);
      if (offered.some((supply) => supply.id === selectedSupply.id)) {
        navigate(Routes.BOOKING);
        return;
      }
      storage.remove(StorageKeys.SELECTED_SUPPLY);
      navigate(Routes.SUPPLY);
    } catch (error) {
      handleError(error);
    } finally {
      setPendingProviderId(null);
    }
  };

  const choose = (providerId) => {
    saveProviderChoice(providerId);
    backToBookingIfOffered(providerId);
  };

  const cards = [...providers.map((provider) => ({ provider, providerId: provider.id })),
    { provider: undefined, providerId: null }];

  return (
    <Page>
      <Typography
        variant="h5"
        component="h1"
        fontWeight="bold"
        textAlign="center"
        mb={{ xs: 3, md: 4 }}
        sx={gradientTextSx(displayConfig, displayConfig?.color)}
      >
        Kihez szeretnél foglalni?
      </Typography>

      <Box
        width="100%"
        maxWidth={1100}
        mx="auto"
        sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 3 }}
      >
        {cards.map(({ provider, providerId }) => (
          <ProviderCard
            key={providerId ?? "any"}
            provider={provider}
            isCurrent={currentChoice?.providerId === providerId}
            // „Bárki” is never checked, and its null id would match the null of nothing pending.
            isPending={providerId !== null && pendingProviderId === providerId}
            disabled={pendingProviderId !== null}
            onClick={() => choose(providerId)}
          />
        ))}
      </Box>
    </Page>
  );
};

export default ProviderChoicePage;
